import React, { useCallback, useMemo } from 'react';
import { RemoveProps, MakeOptional } from 'helpers/typings';
import { HttpParams } from 'resift';
import Autosuggest from 'react-autosuggest';
import { TextFieldProps } from '@mui/material/TextField';
import PersonAutocomplete from './PersonAutocomplete';
import PersonSuggestion from './PersonSuggestion';
import _get from 'lodash/get';
import _flatten from 'lodash/flatten';

interface Props
  extends MakeOptional<
    RemoveProps<TextFieldProps, 'value' | 'onChange' | 'inputProps' | 'rows'>,
    'classes' | 'className' | 'defaultValue' | 'style' | 'InputProps' | 'inputRef'
  > {
  value: string;
  pageSize?: number;
  renderSuggestion?: React.ComponentType<React.PropsWithChildren<any>>;
  onChange: (event: React.FormEvent<any>, params: Autosuggest.ChangeEvent) => void;
  onSelectPerson: (person: any) => void;
  filterSuggestions?: (suggestions: any[]) => any[];
  getEndpoint?: (value: string) => HttpParams;
  getSuggestionsFromResult?: (result: any) => any[];
  labelVisuallyHidden?: boolean;
  emailOnlyResults?: boolean;
  isTeamLeader?: boolean;
  showOrgLevels?: boolean;
  noResultsContent?: React.ReactNode;
}

function getPeopleFromResults(peopleResult: any) {
  const results = _get(peopleResult, ['results'], []);
  return _flatten(results.map((result: any) => _get(result, ['items'], [])));
}

function Container({
  onSelectPerson,
  pageSize,
  renderSuggestion: renderSuggestionFromProps,
  onChange,
  getEndpoint: getEndpointFromProps,
  getSuggestionsFromResult: getSuggestionsFromResultFromProps,
  emailOnlyResults,
  isTeamLeader,
  showOrgLevels,
  ...restOfProps
}: Props) {
  const PersonSuggestionWithOrgLevels = useMemo(
    () => (props: any) => <PersonSuggestion {...props} showOrgLevels />,
    [],
  );

  const getEndpoint = useCallback(
    (value: string) => ({
      method: 'GET' as const,
      route: '/search/people',
      query: {
        q: value,
        pageSize: pageSize ? pageSize.toString() : '5',
        separated: 'false',
        email_exists: emailOnlyResults,
        isTeamLeader,
      },
    }),
    [emailOnlyResults, pageSize, isTeamLeader],
  );

  const handleChange = (e: any, params: Autosuggest.ChangeEvent) => {
    onChange(e, params);
  };

  const handleSuggestionSelected = (_: any, data: any) => {
    const person = data.suggestion;
    onSelectPerson(person);
  };

  return (
    <PersonAutocomplete
      getEndpoint={(getEndpointFromProps || getEndpoint) as any}
      renderSuggestion={
        renderSuggestionFromProps ||
        (showOrgLevels ? PersonSuggestionWithOrgLevels : PersonSuggestion)
      }
      getSuggestionsFromResult={getSuggestionsFromResultFromProps || getPeopleFromResults}
      getSuggestionValue={person => person.displayName}
      onChange={handleChange}
      AutosuggestProps={{
        onSuggestionSelected: handleSuggestionSelected,
        highlightFirstSuggestion: true,
        ..._get(restOfProps, ['AutosuggestProps']),
      }}
      {...restOfProps}
    />
  );
}

export default Container;
